import { useEffect, useRef } from 'react'
import { FeaturedSurface, MixpanelEvent, mixpanelService } from 'src/service/mixpanel-service'

/**
 * Emit `FEATURED_SHELF_VIEWED` once, when a populated shelf first renders.
 *
 * **This is the denominator.** Without it, every rate the feature is judged on — how many
 * people who saw free workouts played one, how many who saw them signed up — is uncomputable,
 * because you only ever had the numerator. It is the cheapest event here and the one most
 * likely to be missed.
 *
 * Fires once per mount and only for a non-empty shelf: an empty set renders nothing, so
 * counting it as "viewed" would inflate the denominator with impressions that never happened.
 * Shared by all three placements — Home shelf, sign-in hero panel, phone rail — so their view
 * counts are defined identically and `surface` is the only thing that differs.
 */
export const useFeaturedShelfViewed = ({ surface, workoutCount }: { surface: FeaturedSurface; workoutCount: number }): void => {
  const hasFiredRef = useRef(false)

  useEffect(() => {
    if (hasFiredRef.current || workoutCount === 0) return
    hasFiredRef.current = true
    mixpanelService.trackFeatured(MixpanelEvent.FEATURED_SHELF_VIEWED, { surface, featuredWorkoutCount: workoutCount })
  }, [surface, workoutCount])
}
